import { Download, Filter, GripVertical, Plus, X } from 'lucide-react';
import { PortalDsButton, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  GRID_SPAN_CLASS,
  METRIC_TONE_CLASSES,
  STUDIO_BLOCK_TYPES,
  STUDIO_METRICS,
} from './studio-dashboard.constants';
import type { StudioBlock } from './studio-dashboard.types';

interface StudioDashboardBlockProps {
  block: StudioBlock;
  onIncreaseSpan: () => void;
  onDecreaseSpan: () => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDrop: () => void;
}

const CHART_LEGEND = [
  { label: 'Clicks', colour: 'bg-[#F59E0B]' },
  { label: 'Conversions', colour: 'bg-[#2563EB]' },
  { label: 'Impressions', colour: 'bg-[#059669]' },
  { label: 'Spend', colour: 'bg-[#EF4444]' },
  { label: 'CTR', colour: 'bg-[#7C3AED]' },
  { label: 'CPM', colour: 'bg-[#0EA5E9]' },
];

export function StudioDashboardBlock({
  block,
  onIncreaseSpan,
  onDecreaseSpan,
  onRemove,
  onDragStart,
  onDrop,
}: StudioDashboardBlockProps) {
  const metric = STUDIO_METRICS.find((item) => item.id === block.metricId);
  const typeDef = STUDIO_BLOCK_TYPES.find((item) => item.type === block.type);
  const typeLabel = block.type === 'metric' ? 'Metric card' : (typeDef?.label ?? block.type);

  return (
    <div
      draggable
      className={cn(
        GRID_SPAN_CLASS[block.span],
        'cursor-grab overflow-hidden rounded-xl border border-[#EDEEF2] bg-white shadow-[0_1px_2px_rgba(13,15,26,0.05)]'
      )}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', block.id);
        onDragStart();
      }}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        onDrop();
      }}
    >
      <div className="flex flex-wrap items-center gap-1.5 border-b border-[#EDEEF2] px-2 py-1.5">
        <GripVertical className="size-3 shrink-0 rotate-90 text-[#9CA3AF]" />
        <div className={cn(portalDs.caption, 'min-w-0 flex-1 truncate')}>
          {typeLabel}
        </div>
        <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-[#EDEEF2] p-0.5">
          <button
            type="button"
            className="flex size-4 items-center justify-center rounded-[3px] text-[11px] font-bold leading-none text-[#5E6278] disabled:opacity-40"
            disabled={block.span <= 1}
            aria-label="Decrease width"
            onClick={onDecreaseSpan}
          >
            –
          </button>
          <span className="w-2.5 text-center text-[11px] font-bold text-[#0D0F1A]">
            {block.span}
          </span>
          <button
            type="button"
            className="flex size-4 items-center justify-center rounded-[3px] text-[#5E6278] disabled:opacity-40"
            disabled={block.span >= 4}
            aria-label="Increase width"
            onClick={onIncreaseSpan}
          >
            <Plus className="size-2.5" />
          </button>
        </div>
        <PortalDsButton
          variant="ghost"
          size="sm"
          className="size-5 p-0"
          aria-label={`Remove ${typeLabel}`}
          onClick={onRemove}
        >
          <X className="size-3" />
        </PortalDsButton>
      </div>

      <div className="p-5">
        {block.type === 'metric' ? (
          <MetricPreview
            label={metric?.label ?? 'Metric'}
            initial={metric?.initial ?? 'M'}
            tone={metric?.tone}
          />
        ) : null}
        {block.type === 'chart' ? (
          <ChartPreview caption={typeDef?.caption} />
        ) : null}
        {block.type === 'table' ? (
          <TablePreview caption={typeDef?.caption} />
        ) : null}
        {typeDef?.isGeo ? (
          <GeoPreview
            title={typeDef.title ?? 'Geographic data unavailable'}
            desc={typeDef.desc ?? ''}
          />
        ) : null}
        {typeDef && !typeDef.isGeo && block.type !== 'chart' && block.type !== 'table' && block.type !== 'metric' ? (
          <TabularPreview
            label={typeDef.label}
            columnLabel={typeDef.columnLabel ?? ''}
            emptyText={typeDef.emptyText ?? ''}
          />
        ) : null}
      </div>
    </div>
  );
}

function MetricPreview({
  label,
  initial,
  tone,
}: {
  label: string;
  initial: string;
  tone?: keyof typeof METRIC_TONE_CLASSES;
}) {
  return (
    <div className="flex flex-col gap-3.5">
      <div
        className={cn(
          'flex size-9 items-center justify-center rounded-[10px] text-sm font-bold',
          tone ? `${METRIC_TONE_CLASSES[tone]} text-white` : 'bg-[#EDEEF2] text-[#5E6278]'
        )}
      >
        {initial}
      </div>
      <div>
        <div className="text-[26px] font-bold tracking-[-0.5px] text-[#0D0F1A]">
          —
        </div>
        <div className={cn(portalDs.caption, 'mt-0.5')}>
          {label}
        </div>
      </div>
    </div>
  );
}

function ChartPreview({ caption }: { caption?: string }) {
  return (
    <div>
      <div className="mb-1 text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
        Performance over time
      </div>
      {caption ? (
        <p className={cn(portalDs.caption, 'mb-3')}>
          {caption}
        </p>
      ) : null}
      <div className="mb-3.5 flex flex-wrap gap-3.5">
        {CHART_LEGEND.map((item) => (
          <div
            key={item.label}
            className="flex items-center gap-1.5"
          >
            <div className={cn('size-2 rounded-full', item.colour)} />
            <span className={portalDs.caption}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
      <div className={cn(portalDs.caption, 'flex h-[130px] items-center justify-center rounded-lg border border-dashed border-[#D9DBE3] bg-[#F7F8FA] text-[#9CA3AF]')}>
        Chart preview
      </div>
    </div>
  );
}

function TablePreview({ caption }: { caption?: string }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-3">
        <div className="text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
          Campaign Performance Breakdown
        </div>
        <PortalDsButton
          variant="secondary"
          size="sm"
        >
          <Download className="size-3.5" />
          Download
        </PortalDsButton>
      </div>
      <p className={cn(portalDs.caption, 'mb-2.5')}>
        Campaign / Line Item / Creative
      </p>
      <div className="overflow-hidden rounded-lg border border-[#EDEEF2]">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-2.5 border-b border-[#EDEEF2] bg-[#F7F8FA] px-3.5 py-2.5">
          {['Campaign', 'Clicks', 'Impressions', 'Conversions', 'CTR', 'Cost'].map((heading) => (
            <div
              key={heading}
              className={portalDs.sectionLabel}
            >
              {heading}
            </div>
          ))}
        </div>
        <div className={cn(portalDs.caption, 'px-6 py-6 text-center text-[#9CA3AF]')}>
          {caption}
        </div>
      </div>
    </div>
  );
}

function TabularPreview({
  label,
  columnLabel,
  emptyText,
}: {
  label: string;
  columnLabel: string;
  emptyText: string;
}) {
  return (
    <div>
      <div className="mb-2.5 text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
        {label}
      </div>
      <div className="mb-4 flex justify-between border-b border-[#EDEEF2] pb-2">
        <div className={portalDs.sectionLabel}>
          {columnLabel}
        </div>
        <div className={portalDs.sectionLabel}>
          Impressions
        </div>
      </div>
      <p className={cn(portalDs.caption, 'py-2 text-center text-[#9CA3AF]')}>
        {emptyText}
      </p>
    </div>
  );
}

function GeoPreview({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="px-3 py-5 text-center">
      <div className="mx-auto mb-3.5 flex size-11 items-center justify-center rounded-xl bg-[#EDEEF2] text-[#9CA3AF]">
        <Filter className="size-[18px]" />
      </div>
      <div className="mb-2 text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
        {title}
      </div>
      <p className={cn(portalDs.caption, 'mx-auto max-w-[380px]')}>
        {desc}
      </p>
    </div>
  );
}
